import * as THREE from 'three';

import Camera from './Core/camera';
import Lighting from './Core/lighting';
import Renderer from './Core/renderer';
import Resources from './Core/resources';
import Scene from './Core/scene';
import {
  THEME_ELEMENT_CLASS,
  THEME_ELEMENT_ID,
  ThreeJSRotation,
} from './threejs.helper';

interface SimulatorProps {
  resource: string;
  scale?: number;
  initialRotation: ThreeJSRotation;
  autoRotate?: boolean;
  flying?: boolean;
  isDarkMode?: boolean;
  isDebugMode?: boolean;
}

export default class Simulator {
  _sizes = {
    width: 400,
    height: 400,
  };
  _debug = false;
  _autoRotate = true;
  _flying = false;
  _isDarkMode = true;

  _canvas: HTMLCanvasElement;
  _camera: Camera;
  _lighting: Lighting;
  _renderer: Renderer;
  _resources: Resources;
  _scene: Scene;

  _themeObserver?: MutationObserver;

  constructor(
    canvas: HTMLCanvasElement,
    {
      resource = '',
      scale = 1.0,
      initialRotation = { x: 0, y: 0, z: 0 },
      autoRotate,
      flying,
      isDarkMode,
      isDebugMode,
    }: SimulatorProps
  ) {
    this._autoRotate = autoRotate ?? this._autoRotate;
    this._flying = flying ?? this._flying;
    this._isDarkMode = isDarkMode ?? this._isDarkMode;
    this._debug = isDebugMode ?? this._debug;
    this._canvas = canvas;
    this._scene = new Scene(this);
    this._camera = new Camera(this);
    this._lighting = new Lighting(this);
    this._renderer = new Renderer(this);
    this._resources = new Resources({
      simulation: this,
      resource,
      initialRotation,
      scale,
      onLoad: (model: THREE.Group | THREE.Mesh) => {
        this.scene.add(model);
      },
    });

    this._tick();
    this._initThemeObserver();
  }

  get sizes() {
    return this._sizes;
  }
  get debug() {
    return this._debug;
  }
  get isDarkMode() {
    return this._isDarkMode;
  }
  get autoRotate() {
    return this._autoRotate;
  }
  get camera() {
    return this._camera._camera;
  }
  get canvas() {
    return this._canvas;
  }
  get lighting() {
    return this._lighting;
  }
  get scene() {
    return this._scene._scene;
  }
  get model() {
    return this._resources._model;
  }

  set darkMode(value: boolean) {
    this._isDarkMode = value;
    this.onToggleTheme(value);
  }

  clock = new THREE.Clock();
  _tick() {
    const elapsedTime = this.clock.getElapsedTime();
    this._scene.onTick(elapsedTime);
    this._camera.onTick(elapsedTime);
    this._renderer.onTick(elapsedTime);
    this._lighting.onTick(elapsedTime);
    this._resources.onTick(elapsedTime);
    window.requestAnimationFrame(this._tick.bind(this));
  }

  onChangeMaterialColour(colour: THREE.ColorRepresentation) {
    this._resources.material.color.set(colour);
  }

  onToggleTheme(isDarkMode: boolean) {
    this._scene.onToggleTheme(isDarkMode);
    this._lighting.onToggleTheme(isDarkMode);
    this._resources.onToggleTheme(isDarkMode);
  }

  onToggleDebug(isDebug: boolean) {
    this._scene.onToggleDebug(isDebug);
    this._resources.onToggleDebug(isDebug);
  }

  _initThemeObserver() {
    this._themeObserver = new MutationObserver(this.mutationObserverCB);
    const element = document.body;
    if (element) {
      this.darkMode = element.classList.contains(THEME_ELEMENT_CLASS);
      this._themeObserver.observe(element, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }
  }

  mutationObserverCB = (mutations: MutationRecord[]) => {
    mutations.forEach(({ target, attributeName }) => {
      if (attributeName === 'class') {
        const currentState = (target as HTMLElement).classList.contains(
          THEME_ELEMENT_CLASS
        );
        console.log('Theme Observer Change:', currentState);
        this.onToggleTheme(currentState);
      }
    });
  };

  onUpdateOffset(x: number, y: number) {
    if (this.camera) {
      if (x) this._camera._camera.position.x = 4 + -15 * x;
      if (y) this._camera._camera.position.y = 4 + -15 * y;
    }
  }

  onMouseLeave() {
    if (this.camera) {
      this.camera.position.x = 4;
      this.camera.position.y = 4;
      this.camera.position.z = -10;
    }
  }

  dispose() {
    console.log('Disposing ThreeJS Simulation');
    this._resources.dispose();
    this._camera.dispose();
    this._lighting.dispose();
    this._scene.dispose();
    this._renderer.dispose();

    // if (this._themeObserver) this._themeObserver.disconnect();
    // if (this._resources._model) {
    //   this._scene._scene.remove(this._resources._model);
    //   // this._resources._model = undefined;
    //   delete this._resources._model;
    // }
  }
}
